import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { lastDayOfMonth } from 'date-fns';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { CalendarIcon } from '@app/app/_icons/CalendarIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { useCalendarState } from './useCalendarState';
import { useCalendarDays } from './useCalendarDays';
import { CalendarHeader } from './CalendarHeader';
import { DaysView } from './DaysView';
import { MonthsView } from './MonthsView';
import { YearsView } from './YearsView';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';

interface Props {
    name: string;
    label?: string;
    defaultValue?: Date;
    initialViewDate?: Date;
    placeholder?: string;
    onChange?: (value: Date | null) => void;
    labelAddon?: ReactNode;
    error?: ViolationError;
    format?: string;
    minDate?: Date;
    maxDate?: Date;
    bottomAddon?: ReactNode;
    rightIcon?: ReactNode;
    disabled?: boolean;
    yearRange?: number;
    showResetButton?: boolean;
    isMonthPicker?: boolean;
    readonly?: boolean;
    className?: string;
    alternateFormatting?: boolean;
}

export function DatePicker({
    name,
    label,
    defaultValue,
    initialViewDate,
    placeholder,
    onChange,
    labelAddon,
    error,
    format = 'yyyy-MM-dd',
    minDate,
    maxDate,
    bottomAddon,
    rightIcon,
    disabled,
    yearRange = 10,
    showResetButton = true,
    isMonthPicker = false,
    readonly = false,
    className = '',
    alternateFormatting = false,
}: Props) {
    const datePickerRef = useRef<HTMLDivElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);
    const t = useTranslations();

    const {
        selectedDate,
        setSelectedDate,
        currentMonth,
        setCurrentMonth,
        viewMode,
        setViewMode,
        open,
        setOpen,
        reset,
        navigateMonth,
        navigateYear,
        navigateTenYears,
        currentYear,
        setCurrentYear,
        ViewMode,
    } = useCalendarState(defaultValue, isMonthPicker, initialViewDate);

    const calendarDays = useCalendarDays(currentMonth, currentYear, selectedDate, minDate, maxDate);
    const [showError, handleChange] = useShowErrorState(error, onChange);

    const [focusedDayIndex, setFocusedDayIndex] = useState(0);
    const [focusedMonthIndex, setFocusedMonthIndex] = useState(0);
    const [focusedYearIndex, setFocusedYearIndex] = useState(0);

    const formatDate = useCallback(
        (date: Date | null): string => {
            if (!date) return '';

            let result = format;
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString();
            result = result.replace('dd', day);
            result = result.replace('MM', month);
            result = result.replace('yyyy', year);
            return result;
        },
        [format],
    );

    const handleToggle = useCallback(() => {
        if (readonly) return;
        setOpen(isOpen => !isOpen);
        const initialViewMode = isMonthPicker ? ViewMode.Months : ViewMode.Days;
        if (viewMode !== initialViewMode) {
            setViewMode(initialViewMode);
        }
    }, [viewMode, isMonthPicker, readonly, setOpen, setViewMode, ViewMode]);

    const handleClose = useCallback(() => {
        setOpen(false);
        const initialViewMode = isMonthPicker ? ViewMode.Months : ViewMode.Days;
        setViewMode(initialViewMode);
    }, [isMonthPicker, setOpen, setViewMode, ViewMode]);

    const handleDismiss = useCallback(() => {
        handleClose();
        datePickerRef.current?.focus();
    }, [handleClose]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled || readonly) {
                e.preventDefault();
                return;
            }
            if (!open && (e.code === 'Space' || e.code === 'Enter')) {
                handleToggle();
                e.preventDefault();
            }
            if (e.code === 'Escape') {
                handleDismiss();
            }
        },
        [disabled, readonly, handleDismiss, handleToggle, open],
    );

    const handleDateClick = useCallback(
        (day: { date: Date; isDisabled: boolean }) => {
            if (day.isDisabled) return;

            setSelectedDate(day.date);
            handleChange(day.date);
            handleDismiss();
        },
        [setSelectedDate, handleChange, handleDismiss],
    );

    const handleMonthClick = useCallback(
        (monthIndex: number, isDisabled: boolean) => {
            if (isDisabled) return;

            if (isMonthPicker) {
                const year = currentYear.getFullYear();
                const lastDay = lastDayOfMonth(new Date(year, monthIndex, 1));
                setSelectedDate(lastDay);
                handleChange(lastDay);
                handleDismiss();
            } else {
                setCurrentMonth(prev => {
                    const newDate = new Date(prev);
                    newDate.setMonth(monthIndex);
                    return newDate;
                });
                setViewMode(ViewMode.Days);
            }
        },
        [
            isMonthPicker,
            currentYear,
            setSelectedDate,
            handleChange,
            handleDismiss,
            setCurrentMonth,
            setViewMode,
            ViewMode.Days,
        ],
    );

    const handleYearClick = useCallback(
        (year: number, isDisabled: boolean) => {
            if (isDisabled) return;

            setCurrentYear(prev => {
                const newDate = new Date(prev);
                newDate.setFullYear(year);

                return newDate;
            });
            setViewMode(ViewMode.Months);
        },
        [setCurrentYear, setViewMode, ViewMode.Months],
    );

    const handlePrevMonth = useCallback(() => {
        navigateMonth('prev', yearRange);
    }, [navigateMonth, yearRange]);

    const handleNextMonth = useCallback(() => {
        navigateMonth('next', yearRange);
    }, [navigateMonth, yearRange]);

    const handlePrevYear = useCallback(() => {
        navigateYear('prev');
    }, [navigateYear]);

    const handleNextYear = useCallback(() => {
        navigateYear('next');
    }, [navigateYear]);

    const handlePrevTenYears = useCallback(() => {
        navigateTenYears('prev');
    }, [navigateTenYears]);

    const handleNextTenYears = useCallback(() => {
        navigateTenYears('next');
    }, [navigateTenYears]);

    const handleViewModeToggle = useCallback(() => {
        if (isMonthPicker) {
            setViewMode(currentMode => (currentMode === ViewMode.Months ? ViewMode.Years : ViewMode.Months));
        } else {
            setViewMode(currentMode => {
                if (currentMode === ViewMode.Days) return ViewMode.Months;
                else if (currentMode === ViewMode.Months) return ViewMode.Years;
                else return ViewMode.Days;
            });
        }
    }, [isMonthPicker, setViewMode, ViewMode]);

    const handleReset = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            reset();
            handleChange(null);
        },
        [reset, handleChange],
    );

    useEffect(() => {
        if (!open) return;

        const handleKeyDownEvent = (e: KeyboardEvent) => {
            if (e.code === 'PageUp') {
                e.preventDefault();
                if (viewMode === ViewMode.Years) {
                    handlePrevTenYears();
                } else {
                    handlePrevMonth();
                }
                return;
            }
            if (e.code === 'PageDown') {
                e.preventDefault();
                if (viewMode === ViewMode.Years) {
                    handleNextTenYears();
                } else {
                    handleNextMonth();
                }
                return;
            }

            if (e.code === 'Enter') {
                e.preventDefault();
                if (viewMode === ViewMode.Days) {
                    const day = calendarDays[focusedDayIndex];
                    if (day && !day.isDisabled) {
                        handleDateClick({ date: day.date, isDisabled: day.isDisabled });
                    }
                } else if (viewMode === ViewMode.Months) {
                    const month = focusedMonthIndex;
                    handleMonthClick(month, false);
                } else if (viewMode === ViewMode.Years) {
                    const currentYears = currentYear.getFullYear();
                    const year = currentYears + focusedYearIndex;
                    handleYearClick(year, false);
                }
                return;
            }

            const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.code);
            if (!isArrowKey) return;

            e.preventDefault();

            if (viewMode === ViewMode.Days) {
                const columns = 7;
                const totalDays = calendarDays.length;
                let newIndex = focusedDayIndex;

                switch (e.code) {
                    case 'ArrowLeft':
                        newIndex = focusedDayIndex > 0 ? focusedDayIndex - 1 : totalDays - 1;
                        break;
                    case 'ArrowRight':
                        newIndex = focusedDayIndex < totalDays - 1 ? focusedDayIndex + 1 : 0;
                        break;
                    case 'ArrowUp':
                        newIndex = focusedDayIndex - columns;
                        if (newIndex < 0)
                            newIndex =
                                focusedDayIndex + columns * Math.floor((totalDays - 1 - focusedDayIndex) / columns);
                        break;
                    case 'ArrowDown':
                        newIndex = focusedDayIndex + columns;
                        if (newIndex >= totalDays) newIndex = focusedDayIndex % columns;
                        break;
                    case 'Home':
                        newIndex = 0;
                        break;
                    case 'End':
                        newIndex = totalDays - 1;
                        break;
                }

                let attempts = 0;
                while (calendarDays[newIndex]?.isDisabled && attempts < totalDays) {
                    newIndex = (newIndex + 1) % totalDays;
                    attempts++;
                }

                if (!calendarDays[newIndex]?.isDisabled) {
                    setFocusedDayIndex(newIndex);
                }
            } else if (viewMode === ViewMode.Months) {
                const columns = 3;
                const totalMonths = 12;
                let newIndex = focusedMonthIndex;

                switch (e.code) {
                    case 'ArrowLeft':
                        newIndex = focusedMonthIndex > 0 ? focusedMonthIndex - 1 : totalMonths - 1;
                        break;
                    case 'ArrowRight':
                        newIndex = focusedMonthIndex < totalMonths - 1 ? focusedMonthIndex + 1 : 0;
                        break;
                    case 'ArrowUp':
                        newIndex = focusedMonthIndex - columns;
                        if (newIndex < 0)
                            newIndex =
                                focusedMonthIndex +
                                columns * Math.floor((totalMonths - 1 - focusedMonthIndex) / columns);
                        break;
                    case 'ArrowDown':
                        newIndex = focusedMonthIndex + columns;
                        if (newIndex >= totalMonths) newIndex = focusedMonthIndex % columns;
                        break;
                    case 'Home':
                        newIndex = 0;
                        break;
                    case 'End':
                        newIndex = totalMonths - 1;
                        break;
                }

                setFocusedMonthIndex(newIndex);
            } else if (viewMode === ViewMode.Years) {
                const columns = 4;
                const totalYears = yearRange * 2 + 1;
                let newIndex = focusedYearIndex;

                switch (e.code) {
                    case 'ArrowLeft':
                        newIndex = focusedYearIndex > 0 ? focusedYearIndex - 1 : totalYears - 1;
                        break;
                    case 'ArrowRight':
                        newIndex = focusedYearIndex < totalYears - 1 ? focusedYearIndex + 1 : 0;
                        break;
                    case 'ArrowUp':
                        newIndex = focusedYearIndex - columns;
                        if (newIndex < 0)
                            newIndex =
                                focusedYearIndex + columns * Math.floor((totalYears - 1 - focusedYearIndex) / columns);
                        break;
                    case 'ArrowDown':
                        newIndex = focusedYearIndex + columns;
                        if (newIndex >= totalYears) newIndex = focusedYearIndex % columns;
                        break;
                    case 'Home':
                        newIndex = 0;
                        break;
                    case 'End':
                        newIndex = totalYears - 1;
                        break;
                }

                setFocusedYearIndex(newIndex);
            }
        };

        document.addEventListener('keydown', handleKeyDownEvent);
        return () => document.removeEventListener('keydown', handleKeyDownEvent);
    }, [
        open,
        viewMode,
        ViewMode,
        calendarDays,
        focusedDayIndex,
        focusedMonthIndex,
        focusedYearIndex,
        currentMonth,
        yearRange,
        handleDateClick,
        handleMonthClick,
        handleYearClick,
        handlePrevMonth,
        handleNextMonth,
        handlePrevTenYears,
        handleNextTenYears,
        currentYear,
    ]);

    useChangeEffect(() => {
        if (open) {
            const initialViewMode = isMonthPicker ? ViewMode.Months : ViewMode.Days;
            setViewMode(initialViewMode);

            if (initialViewMode === ViewMode.Days) {
                const selectedIndex = calendarDays.findIndex(day => day.isSelected);
                const todayIndex = calendarDays.findIndex(day => day.isToday);
                const firstEnabledIndex = calendarDays.findIndex(day => !day.isDisabled);
                setFocusedDayIndex(
                    selectedIndex >= 0
                        ? selectedIndex
                        : todayIndex >= 0
                          ? todayIndex
                          : firstEnabledIndex >= 0
                            ? firstEnabledIndex
                            : 0,
                );
            } else if (initialViewMode === ViewMode.Months) {
                const selectedMonthIndex = selectedDate ? selectedDate.getMonth() : new Date().getMonth();
                setFocusedMonthIndex(selectedMonthIndex);
            }
        }
    }, open);

    useEffect(() => {
        if (defaultValue) {
            setSelectedDate(defaultValue);
            setCurrentMonth(defaultValue);
        }
    }, [defaultValue, setSelectedDate, setCurrentMonth]);

    useEffect(() => {
        if (viewMode === ViewMode.Years) {
            const currentYears = currentYear.getFullYear();
            const startYear = currentYears - yearRange;
            const selectedYearIndex = selectedDate ? selectedDate.getFullYear() - startYear : yearRange;
            setFocusedYearIndex(Math.max(0, selectedYearIndex));
        }
    }, [viewMode, ViewMode, selectedDate, yearRange, currentYear]);

    useClickOutside(handleClose, [datePickerRef, calendarRef]);

    return (
        <div className={twMerge('flex flex-col gap-0.5', className)}>
            {label != null && (
                <label className="mb-1.5 inline-flex items-center gap-1 text-sm">
                    {label}
                    {labelAddon != null && labelAddon}
                </label>
            )}

            <input
                type="hidden"
                name={name}
                value={
                    selectedDate ? (alternateFormatting ? formatDate(selectedDate) : selectedDate.toISOString()) : ''
                }
            />

            <div className="flex w-full md:relative">
                <div
                    ref={datePickerRef}
                    className={twJoin(
                        'flex max-w-full flex-grow items-center gap-1',
                        'rounded-md border border-neutral-400',
                        'outline-none focus:border-neutral-500 has-[:focus]:border-neutral-500',
                        disabled && 'pointer-events-none opacity-50',
                        readonly ? 'cursor-default bg-neutral-50' : 'bg-white',
                    )}
                    role="button"
                    onClick={handleToggle}
                    onKeyDown={handleKeyDown}
                    tabIndex={disabled || readonly ? -1 : 0}
                    aria-haspopup="dialog"
                    aria-label={`${label || ''} ${selectedDate ? formatDate(selectedDate) : ''}`}
                    aria-expanded={open}
                    aria-controls={`datepicker-${name}`}
                    aria-disabled={disabled || readonly}>
                    <div
                        className={twJoin(
                            'flex-grow overflow-hidden text-left',
                            selectedDate == null && 'text-black/25',
                        )}>
                        <div className="overflow-hidden text-ellipsis text-nowrap py-2 pl-3">
                            {selectedDate == null ? placeholder : formatDate(selectedDate)}
                        </div>
                    </div>

                    {showResetButton && selectedDate && !readonly && (
                        <button
                            type="button"
                            className="shrink-0 text-neutral-400 hover:text-neutral-600"
                            onClick={handleReset}
                            aria-label={t('general.datePicker.reset', { defaultValue: 'Reset date' })}>
                            <CloseIcon className="size-4" />
                        </button>
                    )}

                    <div className="shrink-0 px-3">
                        {rightIcon == null ? <CalendarIcon className="size-4" /> : rightIcon}
                    </div>
                </div>

                {open && (
                    <div
                        ref={calendarRef}
                        id={`datepicker-${name}`}
                        className={twJoin(
                            'z-40 bg-white p-2 shadow-lg',
                            'md:absolute md:rounded-md md:border md:border-neutral-200 md:animate-in md:fade-in md:slide-in-from-top-10',
                            'md:right-0 md:top-full md:w-64',
                            'fixed inset-x-0 bottom-0 max-h-[80vh] w-full overflow-auto rounded-t-xl border-t border-neutral-200 animate-in slide-in-from-bottom-4',
                            'md:inset-auto md:max-h-none md:overflow-visible',
                        )}
                        aria-label={t('general.datePicker.calendar', { defaultValue: 'Date Picker Calendar' })}>
                        <CalendarHeader
                            viewMode={viewMode}
                            currentMonth={currentMonth}
                            currentYear={currentYear}
                            yearRange={yearRange}
                            onPrevClick={
                                viewMode === ViewMode.Days
                                    ? handlePrevMonth
                                    : viewMode === ViewMode.Years
                                      ? handlePrevTenYears
                                      : handlePrevYear
                            }
                            onNextClick={
                                viewMode === ViewMode.Days
                                    ? handleNextMonth
                                    : viewMode === ViewMode.Years
                                      ? handleNextTenYears
                                      : handleNextYear
                            }
                            onHeaderClick={handleViewModeToggle}
                        />

                        {!isMonthPicker && viewMode === ViewMode.Days && (
                            <DaysView
                                calendarDays={calendarDays}
                                onDateClick={handleDateClick}
                                focusedDayIndex={focusedDayIndex}
                                onFocusedDayChange={setFocusedDayIndex}
                            />
                        )}

                        {viewMode === ViewMode.Months && (
                            <MonthsView
                                currentYear={currentYear}
                                currentMonth={currentMonth}
                                selectedDate={selectedDate}
                                minDate={minDate}
                                maxDate={maxDate}
                                onMonthClick={handleMonthClick}
                                focusedMonthIndex={focusedMonthIndex}
                                onFocusedMonthChange={setFocusedMonthIndex}
                            />
                        )}

                        {viewMode === ViewMode.Years && (
                            <YearsView
                                currentYear={currentYear}
                                selectedDate={selectedDate}
                                yearRange={yearRange}
                                minDate={minDate}
                                maxDate={maxDate}
                                onYearClick={handleYearClick}
                                focusedYearIndex={focusedYearIndex}
                                onFocusedYearChange={setFocusedYearIndex}
                            />
                        )}

                        {bottomAddon && <div className="mt-2 border-t border-neutral-200 pt-2">{bottomAddon}</div>}
                    </div>
                )}
            </div>

            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
